<script lang="ts" setup>
import { ref } from "vue";
import CheckboxTag from "~/components/form/CheckboxTag.vue";
import SelectTag from "~/components/form/SelectTag.vue";
import ItemList from "~/components/product/ItemList.vue";
import PaginateProducts from "~/components/product/PaginateProducts.vue";
import SelectedFeatures from "~/components/product/SelectedFeatures.vue";
import { useHelper } from "~/composables/helper";
import { usePaginate } from "~/composables/paginate";
import { useSearch } from "~/composables/search";
import { STATES } from "~/constants/states";

// Data
const config = useRuntimeConfig();
const router = useRouter();
const route = useRoute();
const { getTaxonomyOptions } = useHelper();

// Composable: Search
const {
    loading,
    selectedState,
    selectedFilters,
    pageNum,
    sortTypes,
    pageLimits,
    selectedSortBy,
    selectedPageLimit,
    fetchResult,
    fetchCWResources,
    setInitialUrlParams,
    products,
    setResult,
    statamicParams,
    doAlgoliaSearch,
    pageMounted,
} = useSearch();

// Composable: Paginate
const { metaCurrentPage, metaTotalPage, paginationSummary, linkPrev, linkNext } = usePaginate();

const listItemsAsGrid = ref(true);
const showFilterMobile = ref(false);

const gradesOptions = ref(await getTaxonomyOptions("grades"));
const subjectsOptions = ref(await getTaxonomyOptions("subjects"));
const seriesOptions = ref(await getTaxonomyOptions("series"));
const filters = ref({
    grades: {
        title: "Grade Level",
        open: true,
        options: gradesOptions.value ?? [],
    },
    subjects: {
        title: "Subject",
        open: true,
        options: subjectsOptions.value ?? [],
    },
    series: {
        title: "Series",
        open: false,
        options: seriesOptions.value ?? [],
    },
});

// Functions
function getFilterOption(typeKey: string) {
    const [type, key] = typeKey.split(":");
    if (type && key) {
        const options = filters.value[type]?.options;
        if (options) {
            for (let i = 0; i < options.length; i++) {
                if (options[i].key == key) {
                    return options[i];
                }
            }
        }
    }
    return null;
}

function getFilterTitle(typeKey: string) {
    const option = getFilterOption(typeKey);
    if (option) {
        return option.value;
    }
    return "";
}

// Below filter functions are use only in mobile view since checkbox is not used.
function removeFilter(typeKey: string) {
    const index = selectedFilters.value.indexOf(typeKey);
    if (index >= 0) {
        selectedFilters.value.splice(index, 1);
    }
}

function addFilter(typeKey: string) {
    const index = selectedFilters.value.indexOf(typeKey);
    if (index < 0) {
        selectedFilters.value.push(typeKey);
    }
}

function toggleFilter(typeKey: string) {
    const index = selectedFilters.value.indexOf(typeKey);
    if (index >= 0) {
        removeFilter(typeKey);
    } else {
        addFilter(typeKey);
    }
}

function clearFilters() {
    selectedFilters.value = [];
}

function setFilterBodyHeight() {
    const filterHeader = document.getElementById("filter-header");
    const filterBody = document.getElementById("filter-body");
    const filterFooter = document.getElementById("filter-footer");

    if (filterHeader && filterBody && filterFooter) {
        let isMobile = false;
        if (
            navigator.userAgent.match(/Android/i) ||
            navigator.userAgent.match(/webOS/i) ||
            navigator.userAgent.match(/iPhone/i) ||
            navigator.userAgent.match(/iPad/i) ||
            navigator.userAgent.match(/iPod/i) ||
            navigator.userAgent.match(/BlackBerry/i) ||
            navigator.userAgent.match(/Windows Phone/i)
        ) {
            isMobile = true;
        }

        let height = screen.height - (filterHeader.offsetHeight + filterFooter.offsetHeight);
        if (!isMobile) {
            height -= 100;
        }
        filterBody.style.maxHeight = height + "px";
    }
}

function enableFilterMobile() {
    showFilterMobile.value = true;
    setTimeout(setFilterBodyHeight, 500);
}

function setSelectedFilters() {
    if (!route.query) {
        return;
    }

    for (const [key, value] of Object.entries(route.query)) {
        // Add filters if key is other than search, sortBy, limit
        if (key !== "search" && key !== "sortBy" && key !== "limit") {
            for (const option of String(value).split(",")) {
                addFilter(key + ":" + option);
            }
        }
    }
}

watch(selectedState, (newValue, oldValue) => {
    if (process.client) {
        useUserStore().updateUserState(newValue);
    }
});

setSelectedFilters();
setInitialUrlParams();

// Initial products fetch. This is needed to avoid hydration mismatch between server/client.
if (doAlgoliaSearch.value) {
    await fetchResult();
} else {
    const params = statamicParams();
    const searchEndpoint = config.public.STATAMIC_API + "/collections/products/entries";
    const { data } = await useFetch(searchEndpoint, {
        method: "GET",
        query: params,
    });
    setResult(data.value);

    // Now get CW resources for the products
    await fetchCWResources(products.value);
}

// Enable reactive watches only after the initial data fetch is complete,
// so that state changes during setup don't trigger unnecessary navigations.
onMounted(() => {
    pageMounted.value = true;
});
onUnmounted(() => {
    pageMounted.value = false;
});
</script>

<template>
    <div>
        <SelectedFeatures :selected-state="`${selectedState}`" />

        <!-- Filters: Mobile view & list selected filters for both desktop/mobile -->
        <div class="mb-5 flex border-t border-b py-3 md:border-none">
            <div class="flex-none pr-8 md:hidden">
                <span
                    class="text-abc-blue"
                    @click="enableFilterMobile"
                    >Filter</span
                >
                <div
                    v-show="showFilterMobile"
                    class="fixed top-0 left-0 z-50 h-full w-full border-b bg-transparent drop-shadow-lg"
                >
                    <div class="bg-white">
                        <div
                            id="filter-header"
                            class="flex border-b p-4"
                        >
                            <div class="flex-1">
                                <NuxtImg
                                    src="/images/fa/x-regular.svg"
                                    class="w-4"
                                    @click="showFilterMobile = false"
                                />
                            </div>
                            <div class="flex-1 text-center">Filter</div>
                            <div
                                class="text-abc-blue flex-1 cursor-pointer text-right"
                                @click="clearFilters"
                            >
                                Clear All
                            </div>
                        </div>
                        <div
                            id="filter-body"
                            class="overflow-y-auto px-4 pt-4 pb-8"
                        >
                            <SearchBox class="mb-2" />
                            <!-- State -->
                            <SelectTag
                                v-model="selectedState"
                                class="mb-4 w-full"
                            >
                                <option value="">All States</option>
                                <option
                                    v-for="(val, key) in STATES"
                                    :key="key.toLowerCase()"
                                    :value="key.toLowerCase()"
                                >
                                    {{ val }}
                                </option>
                            </SelectTag>
                            <!-- Filters -->
                            <div
                                v-for="(filter, key) in filters"
                                :key="`filter-${key}`"
                                class="border-t p-2"
                            >
                                <div
                                    class="cursor-pointer font-semibold"
                                    @click="filter.open = !filter.open"
                                >
                                    {{ filter.title }}
                                    <span
                                        v-if="filter.open"
                                        class="float-right"
                                        >&minus;</span
                                    >
                                    <span
                                        v-else
                                        class="float-right"
                                        >&plus;</span
                                    >
                                </div>
                                <div
                                    v-if="filter.open"
                                    class="pt-2 pb-4 text-sm"
                                >
                                    <button
                                        v-for="option in filter.options"
                                        :key="`filter-option-${option.key}`"
                                        class="my-1 mr-2 inline-block rounded-md border-2 bg-gray-200 px-2 py-1"
                                        :class="
                                            selectedFilters.indexOf(key + ':' + option.key) >= 0 ? 'border-black' : ''
                                        "
                                        @click="toggleFilter(key + ':' + option.key)"
                                    >
                                        {{ option.value }}
                                    </button>
                                </div>
                            </div>
                        </div>
                        <div
                            id="filter-footer"
                            class="border-t border-b p-4 shadow-xl"
                        >
                            <ButtonPrimary
                                class="w-full"
                                @click="showFilterMobile = false"
                                >See results
                            </ButtonPrimary>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Selected filters list -->
            <div class="flex-1">
                <div v-show="selectedFilters.length > 0">
                    <div
                        v-for="value in selectedFilters"
                        :key="value"
                        class="mr-2 mb-1 inline-block cursor-pointer rounded-lg bg-gray-100 px-4 py-1"
                        @click="removeFilter(value)"
                    >
                        {{ getFilterTitle(value) }}
                        <NuxtImg
                            src="/images/fa/x-solid.svg"
                            alt="Remove"
                            class="ml-4 inline-block w-2"
                        />
                    </div>
                    <span
                        v-if="selectedFilters.length > 1"
                        class="text-abc-blue ml-2 hidden cursor-pointer md:inline"
                        @click="clearFilters()"
                        >Clear All</span
                    >
                </div>
            </div>
        </div>

        <!-- Desktop view: State, top pagination summary, sort, per page limit, grid/list -->
        <div class="mb-4 hidden md:flex">
            <!-- State -->
            <div class="w-60 flex-none">
                <SearchBox class="mb-2" />
                <SelectTag
                    v-model="selectedState"
                    class="w-full"
                >
                    <option value="">All States</option>
                    <option
                        v-for="(val, key) in STATES"
                        :key="key.toLowerCase()"
                        :value="key.toLowerCase()"
                    >
                        {{ val }}
                    </option>
                </SelectTag>
            </div>
            <div class="flex-1 px-10 pt-2">{{ paginationSummary }}</div>
            <!-- Sort -->
            <div class="w-48 flex-none">
                <SelectTag
                    v-model="selectedSortBy"
                    class="w-full"
                >
                    <option value="">Sort</option>
                    <option
                        v-for="obj in sortTypes"
                        :key="obj.id"
                        :value="obj.id"
                    >
                        {{ obj.value }}
                    </option>
                </SelectTag>
            </div>
            <!-- Per page limit -->
            <div class="ml-4 w-48 flex-none">
                <SelectTag
                    v-model="selectedPageLimit"
                    class="w-full"
                >
                    <option value="">Per Page</option>
                    <option
                        v-for="obj in pageLimits"
                        :key="`perPage${obj.id}`"
                        :value="obj.id"
                    >
                        {{ obj.value }}
                    </option>
                </SelectTag>
            </div>
            <!-- Grid/List -->
            <div class="w-32 flex-none pt-1 text-right">
                <NuxtImg
                    v-show="listItemsAsGrid"
                    src="/images/icons/icon_grid-view_selected.svg"
                    class="mr-4 inline-block w-7"
                    alt="Grid Selected"
                />
                <NuxtImg
                    v-show="!listItemsAsGrid"
                    src="/images/icons/icon_grid-view_unselected.svg"
                    class="mr-4 inline-block w-7 cursor-pointer"
                    alt="Grid Unselected"
                    title="Show result in grid"
                    @click="listItemsAsGrid = true"
                />
                <NuxtImg
                    v-show="!listItemsAsGrid"
                    src="/images/icons/icon_list-view_selected.svg"
                    class="inline-block w-7"
                    alt="List Selected"
                />
                <NuxtImg
                    v-show="listItemsAsGrid"
                    src="/images/icons/icon_list-view_unselected.svg"
                    class="inline-block w-7 cursor-pointer"
                    alt="List Unselected"
                    title="Show result in list"
                    @click="listItemsAsGrid = false"
                />
            </div>
        </div>

        <div class="w-full md:flex">
            <!-- Filters: Desktop view  -->
            <div class="hidden w-1/6 flex-none md:block">
                <div
                    v-for="(filter, key) in filters"
                    :key="key"
                    class="border-t p-2"
                >
                    <div
                        class="cursor-pointer font-semibold"
                        @click="filter.open = !filter.open"
                    >
                        {{ filter.title }}
                        <span
                            v-if="filter.open"
                            class="float-right"
                            >&minus;</span
                        >
                        <span
                            v-else
                            class="float-right"
                            >&plus;</span
                        >
                    </div>
                    <div
                        v-if="filter.open"
                        class="pt-2 pb-4 text-sm"
                    >
                        <div
                            v-for="option in filter.options"
                            :key="option.key"
                        >
                            <CheckboxTag
                                v-model="selectedFilters"
                                :value="key + ':' + option.key"
                            >
                                {{ option.value }}
                            </CheckboxTag>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Product list & pagination -->
            <div class="relative flex-1 pt-4 md:pl-10">
                <div
                    v-show="loading"
                    class="text-center"
                >
                    <img
                        src="/images/spinnersBlocksWave.svg"
                        alt="Spinner"
                        class="mx-auto w-20"
                    />
                    Loading...
                </div>
                <div
                    v-if="!loading && Object(products).length > 0"
                    :class="
                        listItemsAsGrid
                            ? 'grid-cols-2 gap-6 sm:grid md:grid-cols-3 lg:gap-10 xl:grid-cols-4 2xl:grid-cols-5'
                            : ''
                    "
                >
                    <ItemList
                        v-for="product in products"
                        :key="product.c_slug"
                        :product="product"
                        :list-items-as-grid="listItemsAsGrid"
                    />
                </div>
                <div
                    v-else-if="!loading"
                    class="pt-10 text-center"
                >
                    No products found.
                    <div class="text-gray-400">Try different search terms or filters.</div>
                </div>

                <!-- Pagination -->
                <PaginateProducts
                    :meta-current-page="metaCurrentPage"
                    :meta-total-page="metaTotalPage"
                    :link-prev="linkPrev"
                    :link-next="linkNext"
                />
            </div>
        </div>
    </div>
</template>
